import {
  Alert,
  AlertBody,
  AlertIcon,
  cn,
  Loader,
  RiErrorWarningLine,
  RiFileUploadLine,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
} from 'component-library';
import { TFunction } from 'i18next';
import { HTMLAttributes, InputHTMLAttributes } from 'react';

import { PlotType } from '@/api/rest/resources/types/plot';

import { PLOT_LAND_TYPES } from '../constants';
import { AreaUnits, ExcelState } from '../types';
import { NONE_COLUMN } from '../utils';

type ExcelPanelProps = {
  t: TFunction;
  excelState: ExcelState | null;
  setExcelState: (fn: (s: ExcelState | null) => ExcelState | null) => void;
  excelColumns: string[];
  isAnalyzing: boolean;
  isDragActive: boolean;
  fileErrors: string[];
  analyzeError: string | undefined;
  excelError: string | undefined;
  getRootProps: (extra?: HTMLAttributes<HTMLElement>) => HTMLAttributes<HTMLElement>;
  getInputProps: () => InputHTMLAttributes<HTMLInputElement>;
};

export const ExcelUploadPanel = ({
  t,
  excelState,
  setExcelState,
  excelColumns,
  isAnalyzing,
  isDragActive,
  fileErrors,
  analyzeError,
  excelError,
  getRootProps,
  getInputProps,
}: ExcelPanelProps) => {
  if (!excelState) {
    return (
      <Stack spacing={6}>
        {isAnalyzing ? (
          <Stack spacing={2} className='items-center justify-center py-8'>
            <Loader className='h-6 w-6' />
            <span className='typography-body2 text-text-secondary'>
              {t('shared.uploadCoordinates.excel.analyzing')}
            </span>
          </Stack>
        ) : (
          <div
            {...getRootProps({
              className: cn(
                'flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-8 transition-colors cursor-pointer hover:border-primary-100 hover:bg-neutral-black-4',
                { 'border-primary-100 bg-primary-12': isDragActive },
              ),
            })}
          >
            <input {...getInputProps()} />
            <RiFileUploadLine size={32} className='text-primary-100' />
            <Stack spacing={1} className='items-center text-center'>
              <span className='typography-body1Semibold'>{t('shared.uploadCoordinates.excel.dropzone.title')}</span>
              <span className='typography-body2 text-text-secondary'>
                {t('shared.uploadCoordinates.excel.dropzone.subtitle')}
              </span>
            </Stack>
          </div>
        )}
        {fileErrors.map((err, i) => (
          <Alert key={i} type='error'>
            <AlertIcon>
              <RiErrorWarningLine size={20} />
            </AlertIcon>
            <AlertBody>{err}</AlertBody>
          </Alert>
        ))}
        {analyzeError && (
          <Alert type='error'>
            <AlertIcon>
              <RiErrorWarningLine size={20} />
            </AlertIcon>
            <AlertBody>{analyzeError}</AlertBody>
          </Alert>
        )}
      </Stack>
    );
  }

  const columnFields = [
    { key: 'latColumn' as const, label: t('shared.uploadCoordinates.excel.latitude'), value: excelState.latColumn },
    { key: 'lngColumn' as const, label: t('shared.uploadCoordinates.excel.longitude'), value: excelState.lngColumn },
    { key: 'areaColumn' as const, label: t('shared.uploadCoordinates.excel.area'), value: excelState.areaColumn },
    { key: 'nameColumn' as const, label: t('shared.uploadCoordinates.excel.plotName'), value: excelState.nameColumn },
  ];

  return (
    <Stack spacing={4}>
      <span className='typography-overline text-text-secondary'>{t('shared.uploadCoordinates.excel.mapColumns')}</span>

      {excelState.sheetNames.length > 0 && (
        <Stack spacing={1}>
          <label className='typography-body2Semibold'>{t('shared.uploadCoordinates.excel.sheet')}</label>
          <Select
            value={excelState.selectedSheet}
            onValueChange={(v) =>
              setExcelState((s) => {
                if (!s) return s;
                const cols = s.columnsBySheet[v as string] ?? [];
                return {
                  ...s,
                  selectedSheet: v,
                  latColumn: cols[0] ?? '',
                  lngColumn: cols[1] ?? '',
                  areaColumn: NONE_COLUMN,
                  nameColumn: NONE_COLUMN,
                };
              })
            }
          >
            <SelectTrigger />
            <SelectContent>
              {excelState.sheetNames.map((sheet) => (
                <SelectItem key={sheet} value={sheet}>
                  {sheet}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
      )}

      {columnFields.map(({ key, label, value }) => (
        <Stack key={key} spacing={1}>
          <label className='typography-body2Semibold'>{label}</label>
          <Select value={value} onValueChange={(v) => setExcelState((s) => (s ? { ...s, [key]: v } : s))}>
            <SelectTrigger placeholder={t('shared.uploadCoordinates.excel.noneOption')} />
            <SelectContent>
              <SelectItem value={NONE_COLUMN}>{t('shared.uploadCoordinates.excel.noneOption')}</SelectItem>
              {excelColumns.map((col) => (
                <SelectItem key={col} value={col}>
                  {col}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Stack>
      ))}

      {excelState.areaColumn !== NONE_COLUMN && (
        <Stack spacing={1}>
          <label className='typography-body2Semibold'>{t('shared.uploadCoordinates.excel.areaUnits.label')}</label>
          <Select
            value={excelState.areaUnits}
            onValueChange={(v) => setExcelState((s) => (s ? { ...s, areaUnits: v as AreaUnits } : s))}
          >
            <SelectTrigger />
            <SelectContent>
              <SelectItem value='square_meters'>
                {t('shared.uploadCoordinates.excel.areaUnits.squareMeters')}
              </SelectItem>
              <SelectItem value='hectares'>{t('shared.uploadCoordinates.excel.areaUnits.hectares')}</SelectItem>
              <SelectItem value='square_kilometers'>
                {t('shared.uploadCoordinates.excel.areaUnits.squareKilometers')}
              </SelectItem>
              <SelectItem value='acres'>{t('shared.uploadCoordinates.excel.areaUnits.acres')}</SelectItem>
            </SelectContent>
          </Select>
        </Stack>
      )}

      <Stack spacing={1} className='border-t border-neutral-black-12 pt-4'>
        <label className='typography-body2Semibold'>{t('shared.uploadPlots.landType.label')}</label>
        <Select
          value={excelState.landType}
          onValueChange={(v) => setExcelState((s) => (s ? { ...s, landType: v as PlotType } : s))}
        >
          <SelectTrigger placeholder={t('shared.uploadPlots.landType.placeholder')} />
          <SelectContent>
            {PLOT_LAND_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {t(`global.plotTypes.${type}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Stack>

      {excelError && (
        <Alert type='error'>
          <AlertIcon>
            <RiErrorWarningLine size={20} />
          </AlertIcon>
          <AlertBody>{excelError}</AlertBody>
        </Alert>
      )}
    </Stack>
  );
};
